/**
 * answerPause — when the wall answers what is typed in its field (the designer's rule, `thinking/plans/
 * 2026_11_02_wall_catalogue_in_memory.md` § The client). The ghost carries the words and follows every keystroke; the
 * order of the wall under it waits for the typing to pause:
 *
 *   - 300 ms after the last keystroke on the column surface, 400 ms on the sheet surface (the phone);
 *   - at once on a space, on Enter, and when a pill or a suggestion lands;
 *   - never under 2 typed characters, and clearing the field restores the default order at once.
 *
 * Pure: `answeredQuery` and `answerDelay` decide, `createSettler` holds the one timer. The hook `useSettledQuery`
 * (`useWallPages.ts`) is the thin wrapper React needs; both readers of the wall (`useWallPages`, `useAnonPage`) take its
 * output as the query to ask, so no reader keeps a debounce of its own.
 */
import {composeQuery, parseQuery} from '@core/wall/tsQuery'

export const PAUSE_COLUMN_MS = 300
export const PAUSE_SHEET_MS = 400
/** Fewer typed characters than this reorder nothing. */
export const MIN_TYPED = 2

/**
 * The query the wall answers for what the field holds. A field of fewer than MIN_TYPED characters answers `''`, the
 * default order; beside pills or facet tokens, a lone letter being typed is left out (the pills answer alone). Any other
 * field answers as it stands.
 */
export function answeredQuery(q: string): string {
    const {pills, facets, words} = parseQuery(q)
    const bare = pills.length === 0 && facets.length === 0
    if (bare) return words.trim().length < MIN_TYPED ? '' : q
    const tail = words.trimEnd().split(' ').pop() ?? ''
    if (!words.endsWith(' ') && tail.length === 1) {
        return composeQuery(pills, words.slice(0, words.length - 1), facets).trimEnd()
    }
    return q
}

const pillsAndFacets = (q: string) => {
    const {pills, facets} = parseQuery(q)
    return [...pills, '\n', ...facets].join('\u0000')
}

/** How long the wall waits before it answers the change of the field from `prev` to `next`. */
export function answerDelay(prev: string, next: string, sheet: boolean): number {
    // only a cleared field answers at once; a field cut back to one letter waits like any other change
    if (next.trim() === '') return 0
    if (/\s$/.test(next)) return 0
    if (pillsAndFacets(prev) !== pillsAndFacets(next)) return 0
    return sheet ? PAUSE_SHEET_MS : PAUSE_COLUMN_MS
}

export type Timers = {
    set: (fn: () => void, ms: number) => unknown
    clear: (handle: unknown) => void
}

export type Settler = {
    /** The field now holds `q`; `now` asks to answer without waiting (Enter, a landing pill or suggestion). */
    change: (q: string, options: {sheet: boolean; now?: boolean}) => void
    cancel: () => void
}

/** Holds the one pause: reports each answered query once, when it settles, and drops a pending one that the field has
 *  since typed away from or back. */
export function createSettler(
    initial: string,
    onSettle: (answered: string) => void,
    timers: Timers = {set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>)},
): Settler {
    let settled = answeredQuery(initial)
    let last = initial
    let pending: unknown = null
    const clear = () => {
        if (pending !== null) timers.clear(pending)
        pending = null
    }
    return {
        change(q, {sheet, now = false}) {
            const answered = answeredQuery(q)
            const delay = now ? 0 : answerDelay(last, q, sheet)
            last = q
            clear()
            if (answered === settled) return
            const settle = () => {
                pending = null
                settled = answered
                onSettle(answered)
            }
            if (delay === 0) settle()
            else pending = timers.set(settle, delay)
        },
        cancel: clear,
    }
}
